import React, { useState } from 'react'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { AiOutlineDelete } from 'react-icons/ai'
import { apiPost } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import { DetailItem, CloseButton } from './TrainingShared.js'

const inputClass = 'block w-full rounded-lg border border-[#DFDFDF] bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
const labelClass = 'block text-sm font-medium text-slate-700 mb-1'

const emptyItem = () => ({ kind: 'video', linkUrl: '', file: null })
const emptyForm = () => ({
    title: '',
    description: '',
    dueDate: '',
    recurrence: 'none',
    items: [emptyItem()]
})

// Modal used ONLY for assigning a new assessment.
// Existing assessments are viewed / removed from the "View" modal.
function AssignAssessment({ employee, onClose, onChange }) {
    const [saving, setSaving] = useState(false)
    const [fileInputKey, setFileInputKey] = useState(0)
    const [form, setForm] = useState(emptyForm())

    const employeeId = employee?.employeeId

    const setField = (name, value) => setForm((c) => ({ ...c, [name]: value }))
    const addItem = () => setForm((c) => ({ ...c, items: [...c.items, emptyItem()] }))
    const removeItem = (idx) => setForm((c) => ({ ...c, items: c.items.filter((_, i) => i !== idx) }))
    const setItem = (idx, patch) => setForm((c) => ({
        ...c,
        items: c.items.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    }))

    const submit = async (event) => {
        event.preventDefault()
        if (!employeeId) return
        if (!form.title.trim()) {
            Swal.fire({ icon: 'warning', title: 'Title is required' })
            return
        }
        if (!form.items.length) {
            Swal.fire({ icon: 'warning', title: 'Add at least one material' })
            return
        }

        const body = new FormData()
        body.append('employeeIds', JSON.stringify([employeeId]))
        body.append('title', form.title.trim())
        body.append('description', form.description.trim())
        // body.append('recurrence', form.recurrence || 'none')
        if (form.dueDate) body.append('dueDate', form.dueDate)

        const specs = []
        let fileIndex = 0

        for (const item of form.items) {
            if (item.kind === 'link') {
                if (!/^https?:\/\//i.test(item.linkUrl || '')) {
                    Swal.fire({ icon: 'warning', title: 'Each URL must start with http:// or https://' })
                    return
                }
                specs.push({ kind: 'link', linkUrl: item.linkUrl.trim() })
            } else {
                if (!item.file) {
                    Swal.fire({
                        icon: 'warning',
                        title: `Choose a file for the ${item.kind === 'video' ? 'video' : 'Docs'}`
                    })
                    return
                }
                body.append('files', item.file)
                specs.push({ kind: item.kind, fileIndex })
                fileIndex += 1
            }
        }
        body.append('materials', JSON.stringify(specs))

        setSaving(true)
        try {
            const result = await apiPost(apiPath.trainingAssignments, body)
            if (!result?.data?.success) {
                Swal.fire({ icon: 'error', title: result?.data?.message || 'Could not assign assessment' })
                return
            }
            setForm(emptyForm())
            setFileInputKey((k) => k + 1)
            if (onChange) onChange()
            Swal.fire({
                icon: 'success',
                title: 'Assessment assigned',
                timer: 1400,
                showConfirmButton: false
            })
            onClose()
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: error?.response?.data?.message || 'Could not assign assessment'
            })
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
            <form onSubmit={submit} className='bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col'>
                {/* Header */}
                <div className='flex items-start justify-between p-6 border-b shrink-0'>
                    <div>
                        <h3 className='text-lg font-semibold text-slate-900'>Assign assessment</h3>
                        <p className='text-sm text-gray-500 mt-1'>
                            {employee?.name || 'Employee'}{employee?.code ? ` (${employee.code})` : ''}
                        </p>
                    </div>
                    <CloseButton onClick={onClose} />
                </div>

                {/* Body */}
                <div className='flex-1 overflow-y-auto p-6 space-y-6'>
                    <section>
                        <h4 className='text-sm font-semibold text-slate-900 mb-3'>Employee</h4>
                        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-lg border bg-gray-50 p-4'>
                            <DetailItem label='Employee ID' value={employee?.code} />
                            <DetailItem label='Name' value={employee?.name} />
                            <DetailItem label='Email' value={employee?.email} />
                            <DetailItem label='Department' value={employee?.department} />
                            <DetailItem label='Job title' value={employee?.job} />
                            <DetailItem
                                label='Hire date'
                                value={employee?.hireDate ? dayjs(employee.hireDate).format('MM-DD-YYYY h:mm A') : 'N/A'}
                            />
                        </div>
                    </section>

                    <section>
                        <h4 className='text-sm font-semibold text-slate-900 mb-3'>Assessment details</h4>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            <div>
                                <label htmlFor='assessmentTitle' className={labelClass}>
                                    Title<span className='text-red-500'> *</span>
                                </label>
                                <input
                                    id='assessmentTitle'
                                    className={inputClass}
                                    placeholder='e.g. Workplace safety basics'
                                    value={form.title}
                                    onChange={(e) => setField('title', e.target.value)}
                                />
                            </div>
                            <div>
                                <label htmlFor='assessmentDue' className={labelClass}>Due date</label>
                                <input
                                    id='assessmentDue'
                                    type='date'
                                    className={inputClass}
                                    value={form.dueDate}
                                    onChange={(e) => setField('dueDate', e.target.value)}
                                />
                            </div>
                            <div className='sm:col-span-2'>
                                <label htmlFor='assessmentDescription' className={labelClass}>Description / purpose</label>
                                <textarea
                                    id='assessmentDescription'
                                    rows={3}
                                    className={inputClass}
                                    placeholder='Why is this assessment being assigned?'
                                    value={form.description}
                                    onChange={(e) => setField('description', e.target.value)}
                                />
                            </div>
                        </div>
                    </section>

                    <section>
                        <div className='flex items-center justify-between mb-3'>
                            <h4 className='text-sm font-semibold text-slate-900'>
                                Materials <span className='font-normal text-gray-500'>({form.items.length})</span>
                            </h4>
                            <button type='button' className='text-sm text-[#2f5bff] hover:text-blue-800' onClick={addItem}>
                                + Add material
                            </button>
                        </div>

                        <div className='space-y-3'>
                            {form.items.map((item, idx) => (
                                <div key={idx} className='rounded-lg border bg-slate-50 p-4'>
                                    <div className='flex items-center justify-between mb-3'>
                                        <p className='text-sm font-medium text-slate-900'>Material {idx + 1}</p>
                                        {form.items.length === 1 ? null : (
                                            <button
                                                type='button'
                                                className='text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed'
                                                title='Remove material'
                                                aria-label='Remove material'
                                                onClick={() => removeItem(idx)}
                                            disabled={form.items.length === 1}
                                        >
                                            <AiOutlineDelete className='w-5 h-5' />
                                        </button>)}
                                    </div>
                                    <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
                                        <div>
                                            <label className='block text-xs text-gray-500 mb-1'>Type</label>
                                            <select
                                                className={inputClass}
                                                value={item.kind}
                                                onChange={(e) => setItem(idx, { kind: e.target.value, file: null, linkUrl: '' })}
                                            >
                                                <option value='video'>Video</option>
                                                <option value='pdf'>Docs</option>
                                                <option value='link'>URL</option>
                                            </select>
                                        </div>
                                        <div className='sm:col-span-2'>
                                            {item.kind === 'link' ? (
                                                <>
                                                    <label className='block text-xs text-gray-500 mb-1'>URL</label>
                                                    <input
                                                        className={inputClass}
                                                        placeholder='https://...'
                                                        value={item.linkUrl}
                                                        onChange={(e) => setItem(idx, { linkUrl: e.target.value })}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <label className='block text-xs text-gray-500 mb-1'>
                                                        {item.kind === 'video' ? '.mp4, .webm or .mkv' : '.pdf, .txt, .doc, .ppt, .jpeg or .png'}
                                                    </label>
                                                    <input
                                                        key={`${idx}-${item.kind}-${fileInputKey}`}
                                                        type='file'
                                                        className={`${inputClass} file:mr-3 file:rounded file:border-0 file:bg-gray-200 file:px-1 file:py-0 file:text-sm`}
                                                        accept={item.kind === 'video'
                                                            ? '.mp4,.webm,.mkv'
                                                            : '.pdf,.txt,.doc,.ppt,.jpeg,.png'}
                                                        onChange={(e) => setItem(idx, { file: e.target.files?.[0] || null })}
                                                    />
                                                    {item.file && (
                                                        <p className='text-xs text-gray-500 mt-1 break-all'>{item.file.name}</p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className='flex items-center justify-end gap-3 p-4 border-t shrink-0'>
                    <button
                        type='button'
                        className='text-black bg-[#E1E1E1] font-normal px-10 py-2.5 text-sm rounded'
                        onClick={onClose}
                    >
                        Back
                    </button>
                    <button
                        type='submit'
                        className='bg-LightBlue text-white font-normal text-sm px-10 py-2.5 rounded shadow hover:shadow-lg disabled:opacity-60'
                        disabled={saving}
                    >
                        {saving ? 'Assigning…' : 'Assign'}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AssignAssessment